import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { Field, SectionCard } from "./shared";
import { gridRowsToLines, formatCurrency } from "./LineGrid";
import JournalRows, { blankJournalRow } from "./JournalRows";
import { ACCOUNTING_BASE } from "./accountingNav";

/**
 * JOURNAL VOUCHER — SIMPLE TALLY-STYLE CORRECTION.
 *
 * Standalone replacement for the old thin wrapper around the shared
 * GridVoucherFormPage (which Credit Note, Debit Note and Purchase still use,
 * completely untouched - see those pages). Journal is now its own page so
 * this redesign can never affect any other voucher type:
 *   - Header: Date, Voucher No. (automatic), Reference (optional), Narration
 *     only - no Due Date / Attachment / Vendor helper fields.
 *   - Entry rows: Dr/Cr | Particulars-Ledger | Amount via JournalRows.jsx -
 *     no separate Debit/Credit columns, no Party/Reference/Line-narration
 *     columns, no "Include Bank/Cash ledgers" checkbox at all.
 *   - Single SAVE JOURNAL button: creates the accounting entry immediately
 *     (create-as-draft then post in the same click, exactly like the old
 *     immediateSave path did) - no Draft/Post workflow, no Cancel/Reverse.
 *   - Recent Journals: Date / Voucher No. / Particulars / Amount / Actions
 *     (View | Edit | Print | Delete) - no Status column, since every row
 *     here is always "posted" the moment it exists.
 *
 * Accounting impact: still posted through the exact same
 * /society/accounting/engine/vouchers endpoints every other voucher type
 * uses, with the exact same line shape (gridRowsToLines) - the backend
 * engine, numbering, dependency-based Edit/Delete rules, and audit log are
 * completely unchanged.
 */
function journalParticulars(voucher) {
  const lines = voucher.lines || [];
  const debitLedgers = lines.filter((l) => Number(l.debit) > 0).map((l) => l.ledgerName || "-");
  const creditLedgers = lines.filter((l) => Number(l.credit) > 0).map((l) => l.ledgerName || "-");
  if (!debitLedgers.length && !creditLedgers.length) return voucher.narration || "-";
  return `${debitLedgers.join(" + ")} → ${creditLedgers.join(" + ")}`;
}

export default function JournalVoucherPage() {
  const [rows, setRows] = useState([blankJournalRow(), blankJournalRow()]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [referenceNo, setReferenceNo] = useState("");
  const [narration, setNarration] = useState("");
  const [vouchers, setVouchers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get("/society/accounting/engine/vouchers?voucherType=JOURNAL").catch(() => ({ data: [] }));
      setVouchers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load journal vouchers"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const lines = gridRowsToLines(rows);
  const totalDebit = lines.reduce((s, l) => s + l.debit, 0);
  const totalCredit = lines.reduce((s, l) => s + l.credit, 0);
  const balanced = lines.length >= 2 && Math.round((totalDebit - totalCredit) * 100) / 100 === 0 && totalDebit > 0;

  const resetForm = () => {
    setRows([blankJournalRow(), blankJournalRow()]);
    setReferenceNo("");
    setNarration("");
  };

  const saveJournal = async () => {
    if (!balanced) { toast.error("Total Dr must equal Total Cr (and be greater than zero) before saving"); return; }
    try {
      setBusy(true);
      const draft = await api.post("/society/accounting/engine/vouchers", { voucherType: "JOURNAL", voucherDate: date, narration, referenceNo, lines });
      await api.post(`/society/accounting/engine/vouchers/${draft.data.id}/post`);
      toast.success("Journal saved");
      resetForm();
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not save journal"));
    } finally {
      setBusy(false);
    }
  };

  const deleteJournal = async (v) => {
    if (!window.confirm(`Permanently delete ${v.voucherNumber || "this journal"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/society/accounting/engine/vouchers/${v.id}`);
      toast.success("Journal deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete this journal"));
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Journal Voucher</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Non-cash adjustments and corrections between ledgers. For ordinary bank/cash transactions use Receipt, Payment or Contra instead.</p>
      </div>

      <SectionCard title="Journal entry">
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Voucher No.">
            <Input value="Assigned automatically on save" disabled className="text-[#9CA3AF]" />
          </Field>
          <Field label="Reference">
            <Input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} placeholder="Optional" />
          </Field>
          <div className="md:col-span-3">
            <Field label="Narration"><Textarea rows={2} value={narration} onChange={(e) => setNarration(e.target.value)} /></Field>
          </div>
        </div>

        <div className="mt-4">
          <JournalRows rows={rows} onChange={setRows} />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button type="button" disabled={busy || !balanced} onClick={saveJournal} className="bg-[#111827] text-white hover:bg-[#374151]" title={!balanced ? "Total Dr must equal Total Cr to save" : undefined}>
            <Send className="mr-2 h-4 w-4" /> SAVE JOURNAL
          </Button>
          {!balanced && lines.length > 0 && (
            <span className="text-xs text-[#6B7280]">Difference: {formatCurrency(Math.abs(totalDebit - totalCredit))}</span>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Recent Journals">
        <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Voucher No.</TableHead><TableHead>Particulars</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-[#6B7280]">Loading...</TableCell></TableRow>
              ) : vouchers.slice(0, 20).map((v) => (
                <TableRow key={v.id}>
                  <TableCell>{v.voucherDate}</TableCell>
                  <TableCell className="font-mono text-xs">{v.voucherNumber || "-"}</TableCell>
                  <TableCell>{journalParticulars(v)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(v.totalDebit)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap justify-end gap-2 text-xs font-semibold">
                      <Link to={`${ACCOUNTING_BASE}/vouchers/${v.id}`} className="text-[#111827] hover:underline">View</Link>
                      <Link to={`${ACCOUNTING_BASE}/vouchers/${v.id}/edit`} className="text-[#111827] hover:underline">Edit</Link>
                      <Link to={`${ACCOUNTING_BASE}/vouchers/${v.id}?print=1`} className="text-[#111827] hover:underline">Print</Link>
                      <button type="button" onClick={() => deleteJournal(v)} className="text-red-600 hover:underline">Delete</button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && vouchers.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-[#6B7280]">No journal vouchers yet. Create your first journal above.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </SectionCard>
    </div>
  );
}
